import { embedParentWindow } from "./embedMessages";
import {
  BRIDGE_EVENT,
  BRIDGE_RPC,
  BRIDGE_RPC_RESULT,
  BridgeMethod,
  BridgeRpcRequest,
  readRpcResult,
} from "./nativeBridgeMessages";

/**
 * Calls the desktop app that embeds this page.
 *
 * The engine runs on the user's machine; this process can only ask for things
 * through the frame's parent. Each call carries a `requestId` the desktop app
 * echoes back, which is what lets several be in flight at once and lets a reply
 * arrive out of order without being mistaken for another call's.
 *
 * A module-level singleton rather than a hook, because non-React code -- a store
 * action orchestrating a run -- is the main caller.
 */

/** The one reason that is not the engine's: there is no desktop app to ask. */
export const NO_DESKTOP_APP = "Not running inside the desktop app";

/**
 * Generous: a startRun goes out to gRPC on the far side of two hops, and
 * starts the engine first if it is not up yet.
 */
export const CALL_TIMEOUT_MS = 30000;

interface Pending {
  resolve: (value: unknown) => void;
  reject: (error: Error) => void;
  timer: ReturnType<typeof setTimeout>;
}

export class NativeBridge {
  #pending = new Map<string, Pending>();
  #listeners = new Map<string, Set<(payload: unknown) => void>>();
  #listening = false;
  #nextId = 0;

  /**
   * Whether there is a desktop app to talk to. Synchronous, so a component can
   * decide on render: the Run Automation button is disabled outside the desktop
   * app on this alone.
   */
  public isAvailable(): boolean {
    return embedParentWindow() !== null;
  }

  public async call<T>(method: BridgeMethod, params?: unknown): Promise<T> {
    const parent = embedParentWindow();
    if (!parent) {
      throw new Error(NO_DESKTOP_APP);
    }

    this.listen();

    this.#nextId += 1;
    const requestId = `rpc-${this.#nextId}-${Date.now()}`;

    return new Promise<T>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.#pending.delete(requestId);
        reject(new Error(`${method} timed out after ${CALL_TIMEOUT_MS}ms`));
      }, CALL_TIMEOUT_MS);

      this.#pending.set(requestId, {
        resolve: resolve as (value: unknown) => void,
        reject,
        timer,
      });

      const request: BridgeRpcRequest = { type: BRIDGE_RPC, requestId, method, params };
      // '*' because the packaged parent is served from file:// and has no
      // addressable origin. See the note in embedMessages.
      parent.postMessage(request, "*");
    });
  }

  /** Subscribe to a pushed event, e.g. run progress. Returns the unsubscribe. */
  public on(event: string, callback: (payload: unknown) => void): () => void {
    this.listen();

    const existing = this.#listeners.get(event) ?? new Set();
    existing.add(callback);
    this.#listeners.set(event, existing);

    return () => {
      this.#listeners.get(event)?.delete(callback);
    };
  }

  /**
   * One window listener for the life of the page, attached on first use rather
   * than at import: a module that adds a listener merely by being imported is
   * awkward to test, and pointless in a tab that will never call anything.
   */
  private listen(): void {
    if (this.#listening) return;
    this.#listening = true;

    window.addEventListener("message", event => {
      const message = readRpcResult(event, embedParentWindow());
      if (!message) return;

      if (message.type === BRIDGE_RPC_RESULT) {
        const pending = this.#pending.get(message.requestId);
        // An id we do not know: a reply to a call that already timed out. Dropped
        // rather than raised, since its caller has already been told.
        if (!pending) return;

        this.#pending.delete(message.requestId);
        clearTimeout(pending.timer);

        if (message.error) {
          pending.reject(new Error(message.error.message));
          return;
        }
        pending.resolve(message.result);
        return;
      }

      if (message.type === BRIDGE_EVENT) {
        // Copied before iterating: a listener may unsubscribe itself.
        [...(this.#listeners.get(message.event) ?? [])].forEach(callback => callback(message.payload));
      }
    });
  }

  /** How many calls are still waiting. Exposed so a test can assert no leak. */
  public get pendingCount(): number {
    return this.#pending.size;
  }
}

export const nativeBridge = new NativeBridge();
